import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';

import PropTypes from 'prop-types';

// Lib
import { INSTAGRAM } from '@/lib/socialProfile';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';

const MentionMediaViewerHeader = ({ post, user, isLoading, className }) => {
    const { t } = useTranslation('commons');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone);

    return (
        <div className={className}>
            {!isLoading ? (
                <div className="flex w-full items-center">
                    <Avatar
                        url={null}
                        fallback={post?.username?.[0]}
                        profileType={activeSocialProfile?.social_network_type}
                        size="lg"
                    />
                    <div className="flex-1 px-5">
                        {activeSocialProfile?.social_network_type === INSTAGRAM ? (
                            <a
                                href={getIGUsernameLink(post?.username)}
                                className="text-sm text-icoBlue"
                                target="_blank"
                                rel="noopener noreferrer">
                                {post?.username}
                            </a>
                        ) : (
                            <p className="text-sm text-icoBlue">{post?.username}</p>
                        )}

                        <p className="mt-1 text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {formatDateTime(post?.created, user?.settings?.date_format)}{' '}
                            {formatDateTime(post?.created, user?.settings?.time_format)}
                        </p>
                    </div>
                    {post?.permalink && (
                        <div>
                            <Button
                                as="a"
                                className="min-w-[2rem] rounded px-2"
                                size="sm"
                                variant="secondary"
                                href={post?.permalink}
                                target="_blank"
                                rel="noopener noreferrer">
                                <Icon
                                    icon={activeSocialProfile?.social_network_type}
                                    className="text-sm"
                                />
                                <span className="ml-2 hidden lg:inline">
                                    {t('media_viewer.view_on', {
                                        socialProfileType:
                                            activeSocialProfile?.social_network_type
                                                ?.charAt(0)
                                                ?.toUpperCase() +
                                            activeSocialProfile?.social_network_type?.slice(1)
                                    })}
                                </span>
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex w-full animate-pulse items-center">
                    <span className="block h-[3.125rem] w-[3.125rem] rounded-full bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
                    <div className="flex-1 px-5">
                        <span className="block w-3/4 rounded bg-icoGray-300 py-2 dark:bg-icoDarkMode-deepBlack" />
                        <span className="mt-1 block w-1/2 rounded bg-icoGray-300 py-1.5 dark:bg-icoDarkMode-deepBlack" />
                    </div>
                    <div className="w-[2rem] lg:w-1/3">
                        <span className="block w-full rounded bg-icoGray-300 py-4 dark:bg-icoDarkMode-deepBlack" />
                    </div>
                </div>
            )}
        </div>
    );
};

MentionMediaViewerHeader.propTypes = {
    post: PropTypes.object,
    user: PropTypes.object,
    isLoading: PropTypes.bool,
    className: PropTypes.string
};

export default MentionMediaViewerHeader;
